import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGroupMembership } from '@/hooks/useGroupMembership';
import {
  fetchUserBets,
  fetchSeries,
  fetchLeaderboard,
  fetchAllUserSideBetPicks,
  fetchGlobalSideBets,
} from '@/lib/supabase-queries';
import type { UserSideBetPick } from '@/lib/supabase-queries';
import type { GlobalSideBet } from '@/types/side-bets';
import { DrillInLayout } from '@/components/DrillInLayout';
import MyBets from '@/components/MyBets';
import { Loader2 } from 'lucide-react';

const MyBetsDrillIn = () => {
  const { groupId, userId: targetUserId } = useParams<{ groupId: string; userId?: string }>();
  const { user } = useAuth();
  const { isMember, loading: membershipLoading } = useGroupMembership(groupId, user?.id);
  const [bets, setBets] = useState<any[]>([]);
  const [seriesList, setSeriesList] = useState<any[]>([]);
  const [rank, setRank] = useState<number | null>(null);
  const [leaderboardTotal, setLeaderboardTotal] = useState<number | null>(null);
  const [targetDisplayName, setTargetDisplayName] = useState<string | null>(null);
  const [targetAvatarId, setTargetAvatarId] = useState<number | null>(null);
  const [sideBetPicks, setSideBetPicks] = useState<UserSideBetPick[]>([]);
  const [allSideBets, setAllSideBets] = useState<GlobalSideBet[]>([]);
  const [loading, setLoading] = useState(true);

  const isViewingOther = !!targetUserId && targetUserId !== user?.id;
  const effectiveUserId = targetUserId || user?.id;

  useEffect(() => {
    if (!groupId || !user || !isMember || !effectiveUserId) return;
    const load = async () => {
      const [b, s, leaderboardData, picks, globalSideBets] = await Promise.all([
        fetchUserBets(effectiveUserId, groupId),
        fetchSeries(),
        fetchLeaderboard(groupId),
        fetchAllUserSideBetPicks(effectiveUserId, groupId!),
        fetchGlobalSideBets(),
      ]);
      setBets(b);
      setSeriesList(s);

      const targetEntry = leaderboardData.find((e: any) => e.user_id === effectiveUserId);
      setRank(targetEntry?.rank ?? null);
      setLeaderboardTotal(
        typeof targetEntry?.total_points === 'number' ? targetEntry.total_points : null
      );

      if (isViewingOther) {
        setTargetDisplayName(targetEntry?.display_name ?? null);
        setTargetAvatarId(targetEntry?.avatar_id ?? null);
      }

      setSideBetPicks(picks);
      setAllSideBets(globalSideBets);
      setLoading(false);
    };
    load();
  }, [groupId, user, isMember, effectiveUserId, isViewingOther]);

  if (!membershipLoading && isMember === false) return <Navigate to="/" replace />;

  const pageTitle = isViewingOther
    ? `ההימורים של ${targetDisplayName || '...'}`
    : 'ההימורים שלי';

  return (
    <DrillInLayout title={pageTitle} groupId={groupId!}>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      ) : (
        <div className="container mx-auto px-4 py-6">
          <MyBets
            bets={bets}
            seriesList={seriesList}
            rank={rank}
            leaderboardTotal={leaderboardTotal}
            isViewingOther={isViewingOther}
            targetDisplayName={targetDisplayName}
            targetAvatarId={targetAvatarId}
            sideBetPicks={sideBetPicks}
            allSideBets={allSideBets}
          />
        </div>
      )}
    </DrillInLayout>
  );
};

export default MyBetsDrillIn;
